import { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from '@splitfiesta/i18n';
import type { AggregatedReactionDto } from '@splitfiesta/shared';

import {
  useAddReactionMutation,
  useRemoveReactionMutation,
  useToggleReactionMutation,
} from '@/hooks/queries/useExpenses';

interface UseReactionManagementProps {
  expenseId: string;
  reactions?: AggregatedReactionDto[];
  currentUserId?: string;
  loading?: boolean;
}

export function useReactionManagement({
  expenseId,
  reactions = [],
  currentUserId,
  loading = false,
}: UseReactionManagementProps) {
  const { t } = useTranslation('social');
  const [hoveredReaction, setHoveredReaction] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  // React Query mutations
  const addReactionMutation = useAddReactionMutation(expenseId);
  const removeReactionMutation = useRemoveReactionMutation(expenseId);
  const toggleReactionMutation = useToggleReactionMutation(expenseId);

  // Check if we've reached the 12 unique emoji limit
  const uniqueEmojiCount = reactions.length;
  const canAddNewEmoji = uniqueEmojiCount < 12;

  // Update tooltip position when hovering
  useEffect(() => {
    if (hoveredReaction && buttonRefs.current[hoveredReaction]) {
      const button = buttonRefs.current[hoveredReaction];
      const rect = button!.getBoundingClientRect();

      setTooltipPosition({
        top: rect.top - 8, // 8px above the button
        left: rect.left + rect.width / 2, // Center horizontally
      });
    }
  }, [hoveredReaction]);

  const getHoveredReaction = useCallback(() => {
    return reactions.find(r => r.emoji === hoveredReaction);
  }, [reactions, hoveredReaction]);

  const handleAddReaction = useCallback(
    async (emoji: string) => {
      if (loading) return;

      try {
        await addReactionMutation.mutateAsync(emoji);
      } catch {
        // Error is handled by the mutation's onError callback
      }
    },
    [addReactionMutation, loading]
  );

  const handleRemoveReaction = useCallback(
    async (emoji: string) => {
      if (loading) return;

      try {
        await removeReactionMutation.mutateAsync(emoji);
      } catch {
        // Error is handled by the mutation's onError callback
      }
    },
    [removeReactionMutation, loading]
  );

  const handleToggleReaction = useCallback(
    async (emoji: string, hasReacted: boolean) => {
      if (loading) return;

      try {
        await toggleReactionMutation.mutateAsync({ emoji, hasReacted });
      } catch {
        // Error is handled by the mutation's onError callback
      }
    },
    [toggleReactionMutation, loading]
  );

  const getReactionButtonStyle = useCallback(
    (reaction: AggregatedReactionDto) => {
      return reaction.hasReacted
        ? 'bg-orange-100 dark:bg-orange-400/20 hover:bg-orange-200 dark:hover:bg-orange-400/30 ring-1 ring-orange-200 dark:ring-orange-400/50 [&.btn]:bg-orange-100 dark:[&.btn]:bg-orange-400/20'
        : 'bg-[#F0EFED] dark:bg-gray-700 hover:bg-[#EBEAE8] dark:hover:bg-gray-600 [&.btn]:bg-[#F0EFED] dark:[&.btn]:bg-gray-700';
    },
    []
  );

  const getReactionTextStyle = useCallback(
    (reaction: AggregatedReactionDto) => {
      return reaction.hasReacted
        ? 'text-orange-700 dark:text-orange-300'
        : 'text-gray-700 dark:text-gray-300';
    },
    []
  );

  const getExistingReactions = useCallback(() => {
    return reactions.filter(r => r.hasReacted).map(r => r.emoji);
  }, [reactions]);

  const getSortedReactionUsers = useCallback(
    (reaction: AggregatedReactionDto) => {
      if (!reaction.users) return [];

      return (
        reaction.users
          // Sort: current user first, then by newest first
          .sort((a, b) => {
            // Current user always comes first
            if (a.id === currentUserId) return -1;
            if (b.id === currentUserId) return 1;

            // Then sort by newest first (most recent addedAt)
            if (!a.addedAt || !b.addedAt) return 0;
            return (
              new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime()
            );
          })
          .slice(0, 8)
      );
    },
    [currentUserId]
  );

  const shouldShowMoreUsers = useCallback((reaction: AggregatedReactionDto) => {
    return (reaction.users && reaction.users.length > 8) || reaction.hasMore;
  }, []);

  const getMoreUsersCount = useCallback((reaction: AggregatedReactionDto) => {
    if (!reaction.users) return 0;
    return reaction.users.length > 8 ? reaction.users.length - 8 : 0;
  }, []);

  const hoveredReactionData = getHoveredReaction();

  return {
    // State
    hoveredReaction,
    setHoveredReaction,
    tooltipPosition,
    tooltipRef,
    buttonRefs,

    // Computed values
    canAddNewEmoji,
    hoveredReactionData,

    // Actions
    handleAddReaction,
    handleRemoveReaction,
    handleToggleReaction,

    // Styling helpers
    getReactionButtonStyle,
    getReactionTextStyle,
    getExistingReactions,

    // Tooltip helpers
    getSortedReactionUsers,
    shouldShowMoreUsers,
    getMoreUsersCount,

    // Loading states
    isAddingReaction: addReactionMutation.isPending,
    isRemovingReaction: removeReactionMutation.isPending,
    isTogglingReaction: toggleReactionMutation.isPending,

    // Translation
    t,
  };
}
